import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconChevronDown, IconDownload } from '../components/icons';
import { Modal } from '../components/Modal';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

interface EntryLine {
  id: number;
  type: 'addition' | 'deduction';
  category: string;
  amount: number;
}

interface Entry {
  id: number;
  user_id: number;
  user_name: string;
  employee_code: string | null;
  base_salary: number;
  hourly_rate: number;
  total_additions: number;
  total_deductions: number;
  gross_pay: number;
  net_pay: number;
  lines: EntryLine[];
}

interface RunDetail {
  id: number;
  period_start: string;
  period_end: string;
  status: 'draft' | 'finalized';
  finalized_at: string | null;
  finalized_by_name: string | null;
  entries: Entry[];
}

function formatPeriod(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const s = new Date(start).toLocaleDateString('en-GB', opts);
  const e = new Date(end).toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
  return `${s} – ${e}`;
}

export function PayrollRunDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { lists, settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const [run, setRun] = useState<RunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [encashingFor, setEncashingFor] = useState<Entry | null>(null);
  const [encashForm, setEncashForm] = useState({ leave_type: '', days: '' });
  const [encashErrors, setEncashErrors] = useState<Record<string, string[]>>({});

  const canUpdate = can('hr.update');
  const canApprove = can('hr.approve');
  const isDraft = run?.status === 'draft';

  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<RunDetail>(`/payroll-runs/${id}`);
      setRun(res.data);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load payroll run.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const recalculate = async () => {
    if (!run) return;
    if (
      !(await confirmAction(
        'Recalculate this payroll run from current compensation, pay components, and overtime? Manually added additions/deductions are kept.',
        { confirmLabel: 'Recalculate' }
      ))
    )
      return;
    try {
      await api.post(`/payroll-runs/${run.id}/recalculate`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Recalculate failed.');
    }
  };

  const finalize = async () => {
    if (!run) return;
    if (!(await confirmAction(`Finalize payroll for ${formatPeriod(run.period_start, run.period_end)}? This cannot be undone.`, { confirmLabel: 'Finalize' })))
      return;
    try {
      await api.post(`/payroll-runs/${run.id}/finalize`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Finalize failed.');
    }
  };

  const exportEft = () => {
    if (!run) return;
    downloadFile(api, `/payroll-runs/${run.id}/export-eft`, `payroll-${run.period_start.slice(0, 7)}-eft.csv`);
  };

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const categoryLabel = (line: EntryLine) =>
    labelFor(lists, line.type === 'addition' ? 'payroll_addition_type' : 'payroll_deduction_type', line.category);

  const openEncash = (entry: Entry) => {
    setEncashForm({ leave_type: '', days: '' });
    setEncashErrors({});
    setEncashingFor(entry);
  };

  const submitEncash = async (e: FormEvent) => {
    e.preventDefault();
    if (!encashingFor) return;
    try {
      await api.post(`/payroll-entries/${encashingFor.id}/encash-leave`, encashForm);
      setEncashingFor(null);
      load();
    } catch (err) {
      setEncashErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Encashment failed.'] });
    }
  };

  if (loading) return null;
  if (error || !run) {
    return (
      <>
        <header className="page-header">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/hr/payroll')} />
        </header>
        <div className="card">
          <p className="error">{error ?? 'Payroll run not found.'}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <h1>Payroll: {formatPeriod(run.period_start, run.period_end)}</h1>
        </div>
        <div className="page-header-actions">
          <span className={`badge ${run.status === 'draft' ? 'badge-scheduled' : 'badge-active'}`}>{run.status}</span>
          {canUpdate && isDraft && (
            <button type="button" className="btn btn-ghost" onClick={recalculate}>
              Recalculate
            </button>
          )}
          {canApprove && isDraft && (
            <button type="button" className="btn btn-primary" onClick={finalize}>
              Finalize
            </button>
          )}
          {canApprove && !isDraft && <IconButton icon={IconDownload} label="Export EFT CSV" onClick={exportEft} />}
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/hr/payroll')} />
        </div>
      </header>

      {run.status === 'finalized' && (
        <p className="muted">Finalized{run.finalized_by_name ? ` by ${run.finalized_by_name}` : ''}.</p>
      )}

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Base Salary</th>
                <th>Additions</th>
                <th>Deductions</th>
                <th>Gross</th>
                <th>Net</th>
              </tr>
            </thead>
            <tbody>
              {run.entries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="muted center">
                    No employees on this run.
                  </td>
                </tr>
              ) : (
                run.entries.flatMap((entry) => {
                  const isExpanded = expanded.has(entry.id);
                  const rows = [
                    <tr key={entry.id}>
                      <td>
                        <button
                          type="button"
                          className="btn icon-btn btn-ghost"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          onClick={() => toggleExpanded(entry.id)}
                        >
                          <IconChevronDown
                            className="icon-btn-svg"
                            style={{ transform: isExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
                          />
                        </button>
                      </td>
                      <td className="muted">{entry.employee_code ?? '—'}</td>
                      <td>
                        <strong>{entry.user_name}</strong>
                      </td>
                      <td>{fmtMoney(entry.base_salary)}</td>
                      <td>{fmtMoney(entry.total_additions)}</td>
                      <td>{fmtMoney(entry.total_deductions)}</td>
                      <td>{fmtMoney(entry.gross_pay)}</td>
                      <td>
                        <strong>{fmtMoney(entry.net_pay)}</strong>
                      </td>
                    </tr>,
                  ];

                  if (!isExpanded) return rows;

                  if (entry.lines.length === 0) {
                    rows.push(
                      <tr key={`lines-empty-${entry.id}`}>
                        <td></td>
                        <td colSpan={7} className="muted">
                          No additions or deductions on this entry.
                        </td>
                      </tr>
                    );
                  } else {
                    entry.lines.forEach((line) => {
                      rows.push(
                        <tr key={`line-${line.id}`}>
                          <td></td>
                          <td colSpan={3} className="muted">
                            {line.type === 'addition' ? 'Addition' : 'Deduction'} — {categoryLabel(line)}
                          </td>
                          <td colSpan={4}>{fmtMoney(line.amount)}</td>
                        </tr>
                      );
                    });
                  }

                  if (canUpdate && isDraft) {
                    rows.push(
                      <tr key={`encash-${entry.id}`}>
                        <td></td>
                        <td colSpan={7}>
                          <button type="button" className="btn btn-ghost" onClick={() => openEncash(entry)}>
                            + Encash Leave
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  return rows;
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {encashingFor && (
        <Modal title={`Encash leave — ${encashingFor.user_name}`} onClose={() => setEncashingFor(null)}>
          <form onSubmit={submitEncash} className="form-grid">
            {encashErrors.general && <div className="alert alert-error span-2">{encashErrors.general[0]}</div>}
            <label className="span-2">
              Leave Type *
              <select
                value={encashForm.leave_type}
                onChange={(e) => setEncashForm({ ...encashForm, leave_type: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {listOptions(lists, 'leave_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {encashErrors.leave_type && <small className="error">{encashErrors.leave_type[0]}</small>}
            </label>
            <label className="span-2">
              Days *
              <input
                type="number"
                step="0.5"
                min="0.5"
                value={encashForm.days}
                onChange={(e) => setEncashForm({ ...encashForm, days: e.target.value })}
                required
              />
              <small className="muted">
                Converted to money at the employee's daily rate and added as a payroll addition. Debited immediately from
                their leave balance — capped at what they actually have remaining.
              </small>
              {encashErrors.days && <small className="error">{encashErrors.days[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEncashingFor(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Encash</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
